import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  FileText,
  Tag,
  Building,
  Calendar,
  Star,
  Check,
  Loader,
  RefreshCw
} from 'lucide-react';
import { getQueriesByFilter, getStandardQueries } from '../../services/queryService';

const StandardQueriesSelector = ({ 
  onSelectQuery, 
  onClose, 
  selectedQuery = null,
  className = ""
}) => {
  // State management
  const [queries, setQueries] = useState([]);
  const [filteredQueries, setFilteredQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    sheetSource: 'all',
    queryType: 'all',
    department: 'all',
    documentCategory: 'all',
    requirement: 'all',
    priority: 'all'
  });

  // Available filter options (will be populated from data)
  const [filterOptions, setFilterOptions] = useState({
    sheetSources: [],
    queryTypes: [],
    departments: [],
    documentCategories: [],
    requirements: [],
    priorities: []
  });

  // UI state
  const [expandedQuery, setExpandedQuery] = useState(null);
  const searchInputRef = useRef(null);

  // Load queries on component mount
  useEffect(() => {
    loadQueries();
  }, []);

  // Apply filters and search when dependencies change
  useEffect(() => {
    applyFiltersAndSearch();
  }, [queries, filters, searchText]);

  // Focus search input when component mounts
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Load all queries and extract filter options
  const loadQueries = async () => {
    setLoading(true);
    try {
      const result = await getStandardQueries({ limitCount: 1000 });
      
      if (result.success) {
        setQueries(result.queries);
        extractFilterOptions(result.queries);
      } else {
        console.error('Error loading queries:', result.error);
      }
    } catch (error) {
      console.error('Error loading queries:', error);
    } finally {
      setLoading(false);
    }
  };

  // Extract unique values for filter options
  const extractFilterOptions = (queriesData) => {
    const options = {
      sheetSources: [...new Set(queriesData.map(q => q.sheetSource).filter(Boolean))],
      queryTypes: [...new Set(queriesData.map(q => q.queryType).filter(Boolean))],
      departments: [...new Set(queriesData.map(q => q.department).filter(Boolean))],
      documentCategories: [...new Set(queriesData.map(q => q.documentCategory).filter(Boolean))],
      requirements: [...new Set(queriesData.flatMap(q => q.requirementStages || []).filter(Boolean))],
      priorities: [...new Set(queriesData.map(q => q.priority).filter(Boolean))]
    };
    
    setFilterOptions(options);
  };

  // Apply filters and search
  const applyFiltersAndSearch = () => {
    let filtered = [...queries];

    // Apply filters
    if (filters.sheetSource !== 'all') {
      filtered = filtered.filter(q => q.sheetSource === filters.sheetSource);
    }
    if (filters.queryType !== 'all') {
      filtered = filtered.filter(q => q.queryType === filters.queryType);
    }
    if (filters.department !== 'all') {
      filtered = filtered.filter(q => q.department === filters.department);
    }
    if (filters.documentCategory !== 'all') {
      filtered = filtered.filter(q => q.documentCategory === filters.documentCategory);
    }
    if (filters.requirement !== 'all') {
      filtered = filtered.filter(q => 
        q.requirementStages?.includes(filters.requirement) ||
        q.requirement?.includes(filters.requirement)
      );
    }
    if (filters.priority !== 'all') {
      filtered = filtered.filter(q => q.priority === filters.priority);
    }

    // Apply search
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(q =>
        q.queryDescription?.toLowerCase().includes(searchLower) ||
        q.queryHeader?.toLowerCase().includes(searchLower) ||
        q.documentDescription?.toLowerCase().includes(searchLower) ||
        q.department?.toLowerCase().includes(searchLower) ||
        q.documentCategory?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredQueries(filtered);
  };

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      sheetSource: 'all',
      queryType: 'all',
      department: 'all',
      documentCategory: 'all',
      requirement: 'all',
      priority: 'all'
    });
    setSearchText('');
  };

  // Handle query selection
  const handleSelectQuery = (query) => {
    onSelectQuery(query);
    onClose();
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-xl border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <FileText className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Standard Queries
          </h3>
          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
            {filteredQueries.length} of {queries.length}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => loadQueries()}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="p-4 border-b border-gray-200">
        {/* Search Bar */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search queries by description, header, department..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Filter Toggle */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          
          {(Object.values(filters).some(f => f !== 'all') || searchText) && (
            <button
              onClick={clearFilters}
              className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X className="h-3 w-3" />
              <span>Clear All</span>
            </button>
          )}
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Sheet Source Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sheet Source</label>
              <select
                value={filters.sheetSource}
                onChange={(e) => handleFilterChange('sheetSource', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Sources</option>
                {filterOptions.sheetSources.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>

            {/* Query Type Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Query Type</label>
              <select
                value={filters.queryType}
                onChange={(e) => handleFilterChange('queryType', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                {filterOptions.queryTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Department Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
              <select
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Departments</option>
                {filterOptions.departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            {/* Document Category Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Document Category</label>
              <select
                value={filters.documentCategory}
                onChange={(e) => handleFilterChange('documentCategory', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Categories</option>
                {filterOptions.documentCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Requirement Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Requirement Stage</label>
              <select
                value={filters.requirement}
                onChange={(e) => handleFilterChange('requirement', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Stages</option>
                {filterOptions.requirements.map(req => (
                  <option key={req} value={req}>{req}</option>
                ))}
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Priorities</option>
                {filterOptions.priorities.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Queries List */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading queries...</span>
          </div>
        ) : filteredQueries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No queries found matching your criteria</p>
            <button
              onClick={clearFilters}
              className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
            >
              Clear filters to see all queries
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredQueries.map((query) => (
              <div
                key={query.id}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  selectedQuery?.id === query.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                }`}
                onClick={() => setExpandedQuery(expandedQuery === query.id ? null : query.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Query Header */}
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-gray-900 text-sm">
                        {query.queryHeader || 'No Header'}
                      </h4>
                      {query.priority && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(query.priority)}`}>
                          {query.priority}
                        </span>
                      )}
                    </div>

                    {/* Query Description */}
                    <p className="text-gray-700 text-sm mb-2 line-clamp-2">
                      {query.queryDescription || query.documentDescription || 'No description available'}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {query.queryType && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {query.queryType}
                        </span>
                      )}
                      {query.requirementStages?.map((stage, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                        >
                          {stage}
                        </span>
                      ))}
                      {query.department && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          <Building className="h-3 w-3 mr-1" />
                          {query.department}
                        </span>
                      )}
                    </div>

                    {/* Expanded Details */}
                    {expandedQuery === query.id && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg border text-xs">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {query.sheetSource && (
                            <div>
                              <span className="font-medium text-gray-700">Source:</span>
                              <span className="ml-1 text-gray-600">{query.sheetSource}</span>
                            </div>
                          )}
                          {query.documentCategory && (
                            <div>
                              <span className="font-medium text-gray-700">Category:</span>
                              <span className="ml-1 text-gray-600">{query.documentCategory}</span>
                            </div>
                          )}
                          {query.createdAt && (
                            <div>
                              <span className="font-medium text-gray-700">Created:</span>
                              <span className="ml-1 text-gray-600">
                                {new Date(query.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Select Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectQuery(query);
                    }}
                    className="ml-3 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors flex items-center space-x-1"
                  >
                    <Check className="h-3 w-3" />
                    <span>Select</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StandardQueriesSelector;
